import { useState, useRef } from "react";

const T = {
  bg:"#0B0F1A", surface:"#111827", card:"#1A2235", border:"#1E2D45",
  accent:"#38BDF8", green:"#34D399", yellow:"#FCD34D", red:"#F87171",
  purple:"#A78BFA", orange:"#FB923C", textPri:"#F0F6FF", textSec:"#94A3B8",
  textMute:"#4B5563", codeBg:"#060D18", codeStr:"#86EFAC", codeKw:"#7DD3FC",
  codeFn:"#FDE68A", codeCmt:"#475569", codeNum:"#F9A8D4",
};

function highlight(code) {
  const kws = ["import","from","as","def","class","return","pass","if","else","elif",
    "for","while","in","not","and","or","True","False","None","with","raise","try",
    "except","finally","lambda","yield","super","self","async","await","nonlocal","global"];
  const builtins = ["print","range","len","sleep","append","format","enumerate",
    "isinstance","hasattr","getattr","setattr","list","dict","set","tuple","str","int","float","next"];
  const threadKws = ["Thread","ThreadPoolExecutor","Future","as_completed","submit","map",
    "Lock","RLock","Semaphore","Event","Condition","Queue","asyncio","aiohttp",
    "gather","create_task","run","run_in_executor","get_event_loop","get_running_loop",
    "current_thread","threading","time","random","start","join","acquire","release",
    "name","target","daemon","is_alive","wait","set","notify","notify_all","put","get",
    "task_done","join_queue","cancel","shield","timeout","sleep","result","exception",
    "ProcessPoolExecutor","concurrent","futures","maxsize"];
  return code.split("\n").map((line, li) => {
    if (line.trimStart().startsWith("#")) {
      return <div key={li} style={{minHeight:"1.4em",color:T.codeCmt}}>{line}</div>;
    }
    const tokens = [];
    let i = 0;
    while (i < line.length) {
      if (line[i]==="#") {
        tokens.push(<span key={i} style={{color:T.codeCmt}}>{line.slice(i)}</span>);
        i=line.length; continue;
      }
      if (line[i]==="'" || line[i]==='"') {
        const q=line[i]; let j=i+1;
        while(j<line.length && line[j]!==q) j++;
        tokens.push(<span key={i} style={{color:T.codeStr}}>{line.slice(i,j+1)}</span>);
        i=j+1; continue;
      }
      if (/\d/.test(line[i]) && (i===0||!/\w/.test(line[i-1]))) {
        let j=i; while(j<line.length && /[\d_.x]/.test(line[j])) j++;
        tokens.push(<span key={i} style={{color:T.codeNum}}>{line.slice(i,j)}</span>);
        i=j; continue;
      }
      if (/[a-zA-Z_]/.test(line[i])) {
        let j=i; while(j<line.length && /\w/.test(line[j])) j++;
        const word=line.slice(i,j);
        let color=T.textPri;
        if (kws.includes(word)) color=T.codeKw;
        else if (threadKws.includes(word)) color=T.codeFn;
        else if (builtins.includes(word)) color=T.accent;
        tokens.push(<span key={i} style={{color}}>{word}</span>);
        i=j; continue;
      }
      tokens.push(<span key={i} style={{color:T.textSec}}>{line[i]}</span>);
      i++;
    }
    return <div key={li} style={{minHeight:"1.4em"}}>{tokens}</div>;
  });
}

function CodeBlock({ code, label="python" }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{marginBottom:14,borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        background:"#0D1929",padding:"5px 14px",borderBottom:`1px solid ${T.border}`}}>
        <span style={{fontSize:11,color:T.accent,fontFamily:"monospace",letterSpacing:.5}}>{label}</span>
        <button onClick={()=>{navigator.clipboard.writeText(code);setCopied(true);setTimeout(()=>setCopied(false),1500);}}
          style={{fontSize:11,color:copied?T.green:T.textSec,background:"none",border:"none",cursor:"pointer",padding:"2px 8px",borderRadius:4}}>
          {copied?"✓ copied":"copy"}
        </button>
      </div>
      <div style={{background:T.codeBg,padding:"12px 16px",overflowX:"auto",
        fontFamily:"'JetBrains Mono','Fira Mono',monospace",fontSize:12.5,lineHeight:1.65}}>
        {highlight(code)}
      </div>
    </div>
  );
}

function Difficulty({ level }) {
  const cfg = {
    Easy:   { bg:"rgba(52,211,153,.12)", color:T.green,  border:"rgba(52,211,153,.3)"  },
    Medium: { bg:"rgba(252,211,77,.12)", color:T.yellow, border:"rgba(252,211,77,.3)"  },
    Hard:   { bg:"rgba(248,113,113,.12)",color:T.red,    border:"rgba(248,113,113,.3)" },
  }[level] || {};
  return (
    <span style={{fontSize:10,fontWeight:700,letterSpacing:1,textTransform:"uppercase",
      padding:"2px 8px",borderRadius:20,background:cfg.bg,color:cfg.color,
      border:`1px solid ${cfg.border}`,whiteSpace:"nowrap"}}>
      {level}
    </span>
  );
}

function ExCard({ num, title, difficulty, goal, requirements=[], starter, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,
      marginBottom:16,overflow:"hidden"}}>
      <div style={{padding:"14px 18px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}>
          <span style={{fontSize:11,fontWeight:700,color:T.textMute,letterSpacing:1,textTransform:"uppercase"}}>{num}</span>
          <span style={{fontSize:14,fontWeight:700,color:T.textPri,flex:1}}>{title}</span>
          <Difficulty level={difficulty} />
        </div>
        <p style={{fontSize:13.5,color:T.textSec,lineHeight:1.7,margin:"0 0 10px"}}>{goal}</p>
        {requirements.length>0 && (
          <ul style={{margin:"0 0 12px",paddingLeft:20}}>
            {requirements.map((r,i)=>(
              <li key={i} style={{fontSize:13,color:T.textSec,lineHeight:1.7,marginBottom:2}}>{r}</li>
            ))}
          </ul>
        )}
        {children}
        {starter && (
          <div>
            <div style={{fontSize:11,fontWeight:600,color:T.textMute,letterSpacing:1,
              textTransform:"uppercase",marginBottom:6}}>Starter Skeleton</div>
            <CodeBlock code={starter} />
          </div>
        )}
        <button onClick={()=>setOpen(o=>!o)}
          style={{background:"none",border:`1px solid ${T.border}`,borderRadius:7,
            padding:"5px 12px",cursor:"pointer",fontSize:12,color:T.textMute,fontFamily:"inherit",marginTop:4}}>
          {open?"▾ Hide hint":"▸ Show hint"}
        </button>
        {open && (
          <div style={{marginTop:10,background:"#060D18",border:`1px solid ${T.green}33`,
            borderRadius:8,padding:"10px 14px",fontSize:12.5,color:T.green,fontFamily:"monospace",lineHeight:1.7}}>
            {hintFor(num)}
          </div>
        )}
      </div>
    </div>
  );
}

function hintFor(num) {
  const hints = {
    "1.1": "with ThreadPoolExecutor(max_workers=3) as executor: futures = [executor.submit(fetch, url) for url in urls]. Then for f in futures: print(f.result()).",
    "1.2": "with ThreadPoolExecutor(max_workers=3) as executor: results = list(executor.map(fetch, urls)). You lose the ability to handle individual exceptions per task and results always come in submission order.",
    "1.3": "futures = {executor.submit(fetch, url, delay): url for url, delay in tasks}. for f in as_completed(futures): print(futures[f], f.result()). Import as_completed from concurrent.futures.",
    "1.4": "futures = {executor.submit(risky, n): n for n in range(6)}. for f in futures: try: print(f.result()) except ValueError as e: print(f'failed: {e}'). Wrap f.result() not the submit call.",
    "2.1": "event = threading.Event(). Thread targets call event.wait(). In main: time.sleep(2); event.set(). All waiting threads unblock simultaneously.",
    "2.2": "sem = threading.Semaphore(3). def use_resource(id): with sem: print(f'Worker {id} using'); time.sleep(1); print(f'Worker {id} done'). 'with sem:' acquires on enter, releases on exit.",
    "2.3": "cond = threading.Condition(). Producer: with cond: while len(buf)>=5: cond.wait(); buf.append(item); cond.notify(). Consumer: with cond: while not buf: cond.wait(); item=buf.pop(0); cond.notify().",
    "2.4": "q = queue.Queue(maxsize=5). Producer: q.put(item) — blocks if full. Consumer: item = q.get() — blocks if empty. No explicit Condition or notify needed.",
    "3.1": "t = threading.Thread(target=background_ticker, daemon=True). t.start(). time.sleep(2). # program exits here, daemon thread is killed automatically.",
    "3.2": "Remove daemon=True. The main thread sleeps 2s then exits its own code, but Python's interpreter waits for t to finish — which it never does. Use daemon threads for background housekeeping tasks you don't mind losing on exit.",
    "4.1": "async def say_hello(name): await asyncio.sleep(1); print(f'Hello, {name}'). asyncio.run(say_hello('World')). Calling say_hello('World') without await creates a coroutine object but does NOT execute it — it's lazy.",
    "4.2": "Sequential: await fetch('A',1); await fetch('B',2); await fetch('C',1.5) — total ~4.5s. Concurrent: await asyncio.gather(fetch('A',1), fetch('B',2), fetch('C',1.5)) — total ~2s (max delay).",
    "4.3": "t1=asyncio.create_task(fetch('A',1)); t2=asyncio.create_task(fetch('B',2)); t3=asyncio.create_task(fetch('C',1.5)). Tasks are scheduled immediately. await t1; await t2; await t3 — total still ~2s.",
    "5.1": "lock = asyncio.Lock(). async def deposit(account, amount, lock): async with lock: old = account['balance']; await asyncio.sleep(0.001); account['balance'] = old + amount. Final balance must equal 10*100*1=1000.",
    "5.2": "sem = asyncio.Semaphore(4). async def call_api(i, sem): async with sem: await asyncio.sleep(0.5); print(f'request {i} done'). asyncio.run(asyncio.gather(*[call_api(i,sem) for i in range(20)])).",
    "6.1": "async def producer(q): for i in range(10): await q.put(i); await asyncio.sleep(0.1); await q.put(None). async def consumer(q): while True: item=await q.get(); if item is None: break; print(item).",
    "6.2": "Producer calls q.put(item). Consumer calls q.task_done() after processing. Main: await asyncio.gather(producer(q), *consumers); await q.join() waits until task_done called for every put item. Then cancel consumer tasks.",
    "7.1": "loop = asyncio.get_event_loop(). result = await loop.run_in_executor(None, blocking_task, 5). run_in_executor runs the function in a thread pool so the event loop is free. Use asyncio.ensure_future for the ticker.",
    "7.2": "asyncio wins at scale because coroutines are lightweight language constructs (no OS thread overhead), context switching is cooperative and cheap, and you can have millions of concurrent coroutines. OS threads consume ~1MB of stack each.",
    "8.1": "sem=asyncio.Semaphore(3); lock=asyncio.Lock(); completed=0. async def download(url): async with sem: await asyncio.sleep(random.uniform(0.3,1.0)); nonlocal completed; async with lock: completed+=1; print(f'{completed}/15 done').",
    "8.2": "with ThreadPoolExecutor(max_workers=3) as ex: lock=threading.Lock(); completed=0. def dl(url): nonlocal completed; time.sleep(random.uniform(0.3,1.0)); with lock: completed+=1; print(f'{completed}/15 done'). futures=[ex.submit(dl,u) for u in urls]; [f.result() for f in futures].",
    "Discussion": "1) Thread=OS-level entity with its own stack. Coroutine=language-level, a function that can pause and resume at await points. 2) asyncio.Lock is needed because even though only one coroutine runs at a time, another coroutine can run between two await points — the 'read-then-sleep-then-write' pattern is still a race. 3) When you must call blocking libraries that don't support async. 4) It blocks the event loop's single thread, preventing ALL other coroutines from running. 5) as_completed yields a Future as soon as it resolves — the OS scheduler decides which thread finishes first.",
  };
  return hints[num] || "Think about which primitive (Lock, Semaphore, Event, Condition, Queue) best models the coordination pattern you need.";
}

// ── SECTIONS ──────────────────────────────────────────────────────────────────

function S1() { return (
  <div>
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      Focus: <code style={{color:T.accent,fontFamily:"monospace"}}>concurrent.futures.ThreadPoolExecutor</code> —
      the high-level way to run many tasks on a pool of threads without managing individual{" "}
      <code style={{color:T.accent,fontFamily:"monospace"}}>Thread</code> objects.
    </div>

    <ExCard num="1.1" title="Submit Tasks to a Pool" difficulty="Easy"
      goal='Import ThreadPoolExecutor. Write a function fetch(url) that sleeps 0.5 seconds (simulating a network call) and returns f"{url} -> 200 OK". Using a pool of max_workers=3, submit 6 URLs with executor.submit(), collect the Future objects, and print each result as it becomes available.'
      starter={`from concurrent.futures import ThreadPoolExecutor
import time

def fetch(url):
    time.sleep(0.5)
    return f"{url} -> 200 OK"

urls = [f"http://site{i}.com" for i in range(6)]
# TODO: create a pool, submit all urls, print results
`} />

    <ExCard num="1.2" title="map() Instead of submit()" difficulty="Easy"
      goal='Redo Exercise 1.1 using executor.map(fetch, urls) instead of manually submitting each task. Compare how much simpler the code is, and note one thing you lose by using map() instead of submit() — think about result ordering vs completion order.'
      requirements={[
        "Use executor.map() to replace the loop of executor.submit() calls",
        "Results from map() always come in submission order — does submit() + as_completed() give the same ordering?",
        "Note: with map(), individual exceptions are re-raised when you iterate results, not per-Future",
      ]} />

    <ExCard num="1.3" title="as_completed() for Fastest-First Results" difficulty="Medium"
      goal='Write a function fetch(url, delay) that sleeps for a variable delay and returns the url. Submit 5 tasks with delays [2, 0.5, 1, 3, 0.2] seconds to a pool, then use concurrent.futures.as_completed() to print results in the order they actually finish — not the order submitted.'
      requirements={[
        "Import as_completed from concurrent.futures",
        "Use a dict mapping Future → url so you can identify which URL completed",
        "Print results as each finishes — the 0.2s task should print first",
      ]}
      starter={`from concurrent.futures import ThreadPoolExecutor, as_completed
import time

def fetch(url, delay):
    time.sleep(delay)
    return url

tasks = [("http://a.com", 2), ("http://b.com", 0.5),
         ("http://c.com", 1), ("http://d.com", 3), ("http://e.com", 0.2)]

with ThreadPoolExecutor(max_workers=5) as executor:
    # TODO: submit all tasks, use as_completed to print in finish order
    pass
`} />

    <ExCard num="1.4" title="Handling Exceptions from a Pool" difficulty="Medium"
      goal='Write a function risky(n) that raises a ValueError if n is even, otherwise returns n * 2. Submit risky(n) for n in range(6) to a pool. For each Future, call .result() inside a try/except block and print either the result or f"n={n} failed: {error}" — one failing task must not crash the whole program.'
      requirements={[
        "risky(0), risky(2), risky(4) must raise ValueError",
        "risky(1), risky(3), risky(5) must return the doubled value",
        "Wrap f.result() — not executor.submit() — in the try/except",
        "All 6 results (or errors) must be printed before the program exits",
      ]} />
  </div>
);}

function S2() { return (
  <div>
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      Focus: higher-level synchronization primitives beyond{" "}
      <code style={{color:T.accent,fontFamily:"monospace"}}>Lock</code>/{" "}
      <code style={{color:T.accent,fontFamily:"monospace"}}>RLock</code>:{" "}
      <code style={{color:T.accent,fontFamily:"monospace"}}>Event</code>,{" "}
      <code style={{color:T.accent,fontFamily:"monospace"}}>Semaphore</code>,{" "}
      <code style={{color:T.accent,fontFamily:"monospace"}}>Condition</code>, and{" "}
      <code style={{color:T.accent,fontFamily:"monospace"}}>queue.Queue</code>.
    </div>

    <ExCard num="2.1" title="Event for Signaling" difficulty="Medium"
      goal='Write a function waiter(event) that prints "waiting...", calls event.wait(), then prints "go!" once unblocked. Start 3 waiter threads, sleep 2 seconds in the main thread, then call event.set() and confirm all 3 threads print "go!" at roughly the same time.'
      requirements={[
        "event = threading.Event() — shared across all threads",
        "All 3 threads must be started before the main thread sleeps",
        "event.set() unblocks ALL waiting threads simultaneously",
        "Join all threads before the program exits",
      ]}
      starter={`import threading
import time

def waiter(event):
    print("waiting...")
    event.wait()
    print("go!")

event = threading.Event()
# TODO: start 3 threads, sleep 2s, set event, join threads
`} />

    <ExCard num="2.2" title="Semaphore to Limit Concurrency" difficulty="Medium"
      goal='You have 10 "workers" but only 3 can use a shared resource (e.g. a database connection) at once. Create a threading.Semaphore(3). Write a function use_resource(worker_id) that acquires the semaphore, prints the "using" message, sleeps 1 second, prints the "done" message, then releases. Launch 10 threads and confirm at most 3 are ever "using resource" at the same time by checking the printed timestamps.'
      requirements={[
        "Use with sem: so release happens even on exception",
        "Include a timestamp in each print: f'{time.strftime(\"%H:%M:%S\")} Worker {id} ...'",
        "Observe that workers 4–10 wait until one of the first 3 finishes",
      ]} />

    <ExCard num="2.3" title="Condition for Producer/Consumer" difficulty="Hard"
      goal='Implement a shared buffer (a plain list, max size 5) protected by a threading.Condition. Write a producer() that appends a new item every 0.2s — waiting on condition.wait() if the buffer is full, calling condition.notify() after appending. Write a consumer() that removes an item every 0.5s — waiting if the buffer is empty, calling condition.notify() after removing. Run one producer and one consumer for 5 seconds, then confirm no items were lost or duplicated.'
      requirements={[
        "Always check the predicate (len(buf)>=5 or not buf) in a while loop, not an if statement",
        "Acquire the Condition (via with cond:) before calling cond.wait() or cond.notify()",
        "Track a total_produced and total_consumed counter to verify no items lost",
        "Use a stop_event (threading.Event) to signal both threads to exit cleanly",
      ]} />

    <ExCard num="2.4" title="queue.Queue — the Easier Producer/Consumer" difficulty="Medium"
      goal='Redo Exercise 2.3 using queue.Queue(maxsize=5) instead of a manual Condition. Notice Queue already handles the blocking/notifying internally via .put() and .get(). Answer: which version was easier to get correct, and why might you still write your own Condition-based version in some situations?'
      requirements={[
        "q = queue.Queue(maxsize=5). q.put(item) blocks if full. q.get() blocks if empty.",
        "Use a sentinel value (None) sent by the producer to signal the consumer to stop",
        "Count items produced and consumed to verify correctness",
        "In a comment, explain one scenario where Condition gives more control than Queue",
      ]} />
  </div>
);}

function S3() { return (
  <div>
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      Focus:{" "}
      <code style={{color:T.accent,fontFamily:"monospace"}}>daemon=True</code> threads —
      threads that are silently killed when the main program exits.
      Essential for background tasks like log flushing, heartbeats, or cache refreshers.
    </div>

    <ExCard num="3.1" title="Daemon vs Non-Daemon" difficulty="Easy"
      goal='Write a function background_ticker() that prints "tick" every 0.5 seconds in an infinite loop. Start it as a daemon thread (daemon=True) and let the main program sleep 2 seconds then exit. Confirm the program exits cleanly without waiting for background_ticker to finish.'
      requirements={[
        "Set t.daemon = True before calling t.start(), or pass daemon=True to Thread()",
        "The main program must exit in ~2 seconds, not hang",
        "You should see about 4 'tick' prints before exit",
      ]}
      starter={`import threading
import time

def background_ticker():
    while True:
        print("tick")
        time.sleep(0.5)

t = threading.Thread(target=background_ticker, daemon=True)
t.start()
time.sleep(2)
print("main thread done — exiting")
`} />

    <ExCard num="3.2" title="What Happens Without daemon=True?" difficulty="Easy"
      goal='Repeat Exercise 3.1 but without setting daemon=True. Observe that the program hangs forever after the main thread finishes its 2-second sleep, because Python waits for all non-daemon threads to finish. Kill it manually (Ctrl+C), then explain in one sentence when you would want daemon threads vs regular threads.'
      requirements={[
        "Remove daemon=True (or set it to False explicitly)",
        "Observe: main prints 'done' at 2s but process does not exit",
        "In a comment: daemon threads are for background housekeeping you don't mind losing on exit; regular threads are for work whose result matters",
      ]} />
  </div>
);}

function S4() { return (
  <div>
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      Focus: <code style={{color:T.accent,fontFamily:"monospace"}}>asyncio</code> basics —
      coroutines, the event loop, <code style={{color:T.accent,fontFamily:"monospace"}}>await</code>,
      and the difference between sequential and concurrent async execution.
      You need Python 3.7+ for <code style={{color:T.accent,fontFamily:"monospace"}}>asyncio.run()</code>.
    </div>

    <ExCard num="4.1" title="Your First Coroutine" difficulty="Easy"
      goal='Write an async def function say_hello(name) that prints f"Hello, {name}" after await asyncio.sleep(1). Run it with asyncio.run(say_hello("World")). Then explain: why does calling say_hello("World") directly (without await or asyncio.run) NOT run the function?'
      requirements={[
        "Calling say_hello('World') returns a coroutine object — it does NOT execute",
        "The coroutine only runs when you await it or pass it to asyncio.run()",
        "Add a print before and after asyncio.run() to confirm the 1-second pause",
      ]}
      starter={`import asyncio

async def say_hello(name):
    await asyncio.sleep(1)
    print(f"Hello, {name}")

# This creates a coroutine object but does NOT run it:
# coro = say_hello("World")  # just an object!

asyncio.run(say_hello("World"))  # THIS runs it
`} />

    <ExCard num="4.2" title="Sequential vs Concurrent await" difficulty="Medium"
      goal='Write async def fetch(name, delay) that sleeps delay seconds then prints f"{name} done". First, await three calls sequentially inside a single coroutine and time the total (should be the sum of delays). Then rewrite using asyncio.gather() and time it again (should be close to the max delay). Explain the difference.'
      requirements={[
        "Sequential: await fetch('A',1); await fetch('B',2); await fetch('C',1.5) — total ~4.5s",
        "Concurrent: await asyncio.gather(fetch('A',1), fetch('B',2), fetch('C',1.5)) — total ~2s",
        "Use time.time() to measure and print elapsed time for both approaches",
        "In a comment: sequential awaiting is like blocking one step at a time — gather releases the event loop to run all three concurrently",
      ]}
      starter={`import asyncio
import time

async def fetch(name, delay):
    await asyncio.sleep(delay)
    print(f"{name} done")

async def main():
    # Sequential version
    t0 = time.time()
    await fetch("A", 1)
    await fetch("B", 2)
    await fetch("C", 1.5)
    print(f"Sequential: {time.time()-t0:.1f}s")

    # Concurrent version with gather
    t0 = time.time()
    # TODO: replace with asyncio.gather(...)
    print(f"Concurrent: {time.time()-t0:.1f}s")

asyncio.run(main())
`} />

    <ExCard num="4.3" title="Creating Tasks Explicitly" difficulty="Medium"
      goal='Instead of asyncio.gather, use asyncio.create_task() to schedule 3 fetch() coroutines so they start running immediately (before you await any of them), then await each task afterward. Confirm the total time is still close to the max delay, and explain the difference between just calling a coroutine function and wrapping it in create_task().'
      requirements={[
        "t1 = asyncio.create_task(fetch('A', 1)) — task is scheduled immediately",
        "Create t2 and t3 before awaiting any of them",
        "await t1; await t2; await t3 — all three should already be running",
        "Key insight: create_task() starts the coroutine NOW; a bare coroutine call just creates an object and does nothing",
      ]} />
  </div>
);}

function S5() { return (
  <div>
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      Focus: <code style={{color:T.accent,fontFamily:"monospace"}}>asyncio.Lock</code> and{" "}
      <code style={{color:T.accent,fontFamily:"monospace"}}>asyncio.Semaphore</code> — the async
      equivalents of their threading counterparts. Use <code style={{color:T.accent,fontFamily:"monospace"}}>async with</code>{" "}
      instead of <code style={{color:T.accent,fontFamily:"monospace"}}>with</code>.
    </div>

    <ExCard num="5.1" title="asyncio.Lock" difficulty="Medium"
      goal='Write an async def deposit(account, amount, lock) coroutine that reads the balance, awaits asyncio.sleep(0.001), then writes the new balance — all inside async with lock:. Launch 10 coroutines each calling deposit 100 times via asyncio.gather, and confirm the final balance is exactly 1000 (10 coroutines × 100 deposits × 1 each).'
      requirements={[
        "account = {'balance': 0} — shared mutable state",
        "Without the lock: final balance will be less than 1000 (race condition across await points)",
        "With async with lock: the read-sleep-write sequence is atomic from the event loop's perspective",
        "Print the final balance and assert it equals 1000",
      ]}
      starter={`import asyncio

async def deposit(account, amount, lock):
    async with lock:
        old = account['balance']
        await asyncio.sleep(0.001)  # simulate async I/O
        account['balance'] = old + amount

async def main():
    account = {'balance': 0}
    lock = asyncio.Lock()
    tasks = [deposit(account, 1, lock) for _ in range(10 * 100)]
    await asyncio.gather(*tasks)
    print(f"Final balance: {account['balance']}")  # must be 1000

asyncio.run(main())
`} />

    <ExCard num="5.2" title="asyncio.Semaphore for Rate Limiting" difficulty="Medium"
      goal='You want to call an API but limit to 4 concurrent requests out of 20 total. Write async def call_api(i, semaphore) that does async with semaphore: then await asyncio.sleep(0.5) then prints f"request {i} done". Create an asyncio.Semaphore(4), launch 20 of these coroutines with asyncio.gather, and confirm (via timestamps) that no more than 4 run concurrently.'
      requirements={[
        "sem = asyncio.Semaphore(4)",
        "Include a timestamp in each print: f'{asyncio.get_event_loop().time():.2f} request {i} done'",
        "With 4 concurrent slots and 0.5s each: 20 requests should finish in ~2.5s (5 batches)",
        "Without the semaphore: all 20 would finish in ~0.5s but overwhelm the API",
      ]} />
  </div>
);}

function S6() { return (
  <div>
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      Focus: <code style={{color:T.accent,fontFamily:"monospace"}}>asyncio.Queue</code> for
      async producer/consumer patterns — the async equivalent of{" "}
      <code style={{color:T.accent,fontFamily:"monospace"}}>queue.Queue</code>.
    </div>

    <ExCard num="6.1" title="Async Producer/Consumer" difficulty="Hard"
      goal='Write async def producer(queue) that puts 10 items (numbers 0–9) onto an asyncio.Queue(), sleeping 0.1s between each, then puts a sentinel None to signal "done". Write async def consumer(queue) that loops calling await queue.get(), processes (prints) the item, and stops when it receives None. Run both concurrently with asyncio.gather.'
      requirements={[
        "q = asyncio.Queue() — no maxsize needed for this exercise",
        "await q.put(item) — async put; await q.get() — async get",
        "The consumer must break out of its loop when it sees None",
        "All 10 numbers must be printed before the program exits",
      ]}
      starter={`import asyncio

async def producer(queue):
    for i in range(10):
        await queue.put(i)
        await asyncio.sleep(0.1)
    await queue.put(None)  # sentinel

async def consumer(queue):
    while True:
        item = await queue.get()
        if item is None:
            break
        print(f"consumed: {item}")

async def main():
    q = asyncio.Queue()
    await asyncio.gather(producer(q), consumer(q))

asyncio.run(main())
`} />

    <ExCard num="6.2" title="Multiple Consumers" difficulty="Hard"
      goal='Extend Exercise 6.1 to use 3 consumer coroutines pulling from the same queue. Because multiple consumers see different items, you cannot rely on a single None sentinel. Instead, use queue.join() in the main coroutine: the producer puts items and consumers call q.task_done() after processing each; once all items are processed, cancel the consumer tasks. Confirm all 10 items get processed exactly once across all 3 consumers combined.'
      requirements={[
        "q = asyncio.Queue(). Producer puts 10 items without a sentinel.",
        "Consumer loop: item = await q.get(); process it; q.task_done()",
        "Main: producer_task = asyncio.create_task(producer(q)); await q.join() blocks until all task_done() calls match all put() calls",
        "After q.join() returns, cancel all consumer tasks (for c in consumers: c.cancel())",
        "Track a shared counter (with asyncio.Lock) to confirm total processed == 10",
      ]} />
  </div>
);}

function S7() { return (
  <div>
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      Focus: running blocking (synchronous) code from inside an async program without
      freezing the event loop, using{" "}
      <code style={{color:T.accent,fontFamily:"monospace"}}>loop.run_in_executor()</code>.
    </div>

    <ExCard num="7.1" title="Running Blocking Code from Async" difficulty="Medium"
      goal='time.sleep() is blocking and must never be called directly inside async def — it would freeze the entire event loop. Write a synchronous function blocking_task(n) that calls time.sleep(1) and returns n * n. From an async def main(), use await asyncio.get_event_loop().run_in_executor(None, blocking_task, 5) to run it in a thread pool without blocking the loop. Confirm other coroutines (a ticker printing every 0.2s) keep running concurrently while blocking_task executes.'
      requirements={[
        "blocking_task must use time.sleep(1), not asyncio.sleep",
        "A ticker coroutine (async def ticker()) should print 'tick' every 0.2s for 1.2s",
        "Run both with asyncio.gather(run_in_executor(...), ticker()) — ticker should print 5 times while blocking_task runs",
        "run_in_executor(None, fn, arg) — None means use the default thread pool executor",
      ]}
      starter={`import asyncio
import time

def blocking_task(n):
    time.sleep(1)  # blocking — cannot be replaced with asyncio.sleep here
    return n * n

async def ticker():
    for _ in range(5):
        print("tick")
        await asyncio.sleep(0.2)

async def main():
    loop = asyncio.get_event_loop()
    result, _ = await asyncio.gather(
        loop.run_in_executor(None, blocking_task, 5),
        ticker(),
    )
    print(f"blocking_task result: {result}")

asyncio.run(main())
`} />

    <ExCard num="7.2" title="When to Choose What" difficulty="Medium"
      goal='Write a short async function that fetches 5 "URLs" using asyncio.sleep(0.3) to simulate I/O, and a separate function using ThreadPoolExecutor to do the same 5 fetches with time.sleep(0.3). Time both — they should take about the same wall-clock time (~0.3s). Then in a comment explain: for a program making thousands of concurrent I/O calls, why is asyncio typically more resource-efficient than spawning thousands of OS threads?'
      requirements={[
        "Both approaches should finish in ~0.3s (concurrency not serialization)",
        "asyncio version: await asyncio.gather(*[fetch_async(i) for i in range(5)])",
        "thread version: ThreadPoolExecutor(max_workers=5), executor.map(fetch_sync, range(5))",
        "Comment must mention: OS threads consume ~1MB stack each; coroutines are lightweight Python objects; millions of coroutines are feasible, millions of threads are not",
      ]} />
  </div>
);}

function S8() { return (
  <div>
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
      padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      Put everything together. These exercises combine concurrency primitives, error handling,
      and resource management in realistic scenarios.
    </div>

    <ExCard num="8.1" title="Async Rate-Limited Downloader with Progress" difficulty="Hard"
      goal='Build an async "downloader": 15 fake URLs, an asyncio.Semaphore limiting to 3 concurrent downloads, each download simulated with asyncio.sleep(random.uniform(0.3, 1.0)). Track progress with a shared counter protected by an asyncio.Lock, printing f"{completed}/{total} done" after each download finishes. Run with asyncio.gather and confirm the final line reads "15/15 done".'
      requirements={[
        "sem = asyncio.Semaphore(3); lock = asyncio.Lock(); completed = 0",
        "async def download(url, sem, lock): async with sem: await asyncio.sleep(...); async with lock: update counter",
        "Use nonlocal completed inside the download coroutine (or store in a mutable dict)",
        "asyncio.run(asyncio.gather(*[download(url, sem, lock) for url in urls]))",
        "Final output must include '15/15 done'",
      ]}
      starter={`import asyncio
import random

async def download(url, sem, lock, state):
    async with sem:
        delay = random.uniform(0.3, 1.0)
        await asyncio.sleep(delay)
    async with lock:
        state['completed'] += 1
        print(f"{state['completed']}/{state['total']} done — {url}")

async def main():
    urls = [f"http://cdn.example.com/file{i}" for i in range(15)]
    sem = asyncio.Semaphore(3)
    lock = asyncio.Lock()
    state = {'completed': 0, 'total': len(urls)}
    await asyncio.gather(*[download(url, sem, lock, state) for url in urls])

asyncio.run(main())
`} />

    <ExCard num="8.2" title="Thread Pool Rate-Limited Downloader" difficulty="Hard"
      goal='Reimplement Exercise 8.1 using ThreadPoolExecutor(max_workers=3) and time.sleep instead of asyncio. Compare the two implementations side by side — which one was less code? Which would scale better to 10,000 URLs?'
      requirements={[
        "threading.Lock() for the counter (not asyncio.Lock)",
        "time.sleep(random.uniform(0.3, 1.0)) inside the worker function",
        "ThreadPoolExecutor(max_workers=3) — equivalent to Semaphore(3)",
        "Collect all futures with [executor.submit(download, url) for url in urls]",
        "In a comment: asyncio scales to 10,000+ with minimal memory; ThreadPoolExecutor(max_workers=10000) would create 10,000 OS threads (~10GB RAM)",
      ]} />
  </div>
);}

function SDiscussion() {
  const qs = [
    {
      q: "What's the fundamental difference between what a thread is (OS-level concept) and what a coroutine is (language-level concept)?",
      a: "A thread is an OS-managed execution unit with its own stack (~1MB), scheduled preemptively by the kernel — the OS can switch between threads at any instruction. A coroutine is a Python language construct: a function that voluntarily pauses at await points, returning control to the event loop. Coroutines are cooperative, not preemptive — a coroutine runs until it awaits something.",
    },
    {
      q: "Why is asyncio.Lock needed at all if only one coroutine can run 'at a time' on the event loop — isn't the code already safe from races?",
      a: "It's safe only between await points. If a coroutine reads a value, then awaits something, then writes — another coroutine can run during that await and modify the same value. The 'read → await → write' sequence is NOT atomic. asyncio.Lock ensures only one coroutine can be between the read and the write at any time.",
    },
    {
      q: "When would ThreadPoolExecutor be a better choice than asyncio, even for I/O-bound work?",
      a: "When your I/O library is synchronous and can't be made async — e.g. the standard requests library, or a database driver that doesn't have an async version. run_in_executor bridges sync code into async, but if most of your code is sync, using a thread pool directly is simpler than building an async wrapper around it.",
    },
    {
      q: "Why must you never put a blocking call like time.sleep() or a synchronous requests.get() directly inside an async def?",
      a: "The event loop is single-threaded. A blocking call occupies the thread entirely — no other coroutine can run while it's sleeping or waiting for network I/O. One accidental time.sleep(5) inside an async handler freezes every concurrent request being served by that event loop for 5 full seconds.",
    },
    {
      q: "In Exercise 1.3, why does as_completed() give you results in a different order than the order you submitted the tasks?",
      a: "as_completed() wraps each Future in an internal done-callback that signals a queue when the Future resolves. The OS thread scheduler decides which thread finishes first, independent of submission order. The 0.2s task finishes first not because it was submitted last but because it sleeps the shortest — wall-clock completion order drives as_completed(), not submission order.",
    },
  ];

  const [open, setOpen] = useState({});
  return (
    <div>
      <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,
        padding:"12px 16px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
        No code required. Think through each question, then reveal the answer.
        These distinctions come up in every async/threading interview.
      </div>
      {qs.map((item, i) => (
        <div key={i} style={{background:T.card,border:`1px solid ${T.border}`,
          borderRadius:12,marginBottom:14,overflow:"hidden"}}>
          <button onClick={()=>setOpen(o=>({...o,[i]:!o[i]}))}
            style={{width:"100%",textAlign:"left",background:"none",border:"none",
              padding:"14px 18px",cursor:"pointer",display:"flex",gap:12,alignItems:"flex-start"}}>
            <span style={{color:T.purple,fontWeight:700,fontSize:13,minWidth:24}}>Q{i+1}</span>
            <span style={{fontSize:13.5,color:T.textPri,lineHeight:1.6,flex:1,fontFamily:"inherit"}}>{item.q}</span>
            <span style={{color:T.textMute,fontSize:12}}>{open[i]?"▾":"▸"}</span>
          </button>
          {open[i] && (
            <div style={{padding:"0 18px 14px 52px",fontSize:13,color:T.textSec,lineHeight:1.75}}>
              {item.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── SIDEBAR METADATA ──────────────────────────────────────────────────────────

const SECTIONS = [
  { id:"home",  icon:"🏠", title:"Overview",                    sub:"All 8 sections at a glance" },
  { id:"s1",    icon:"🏊", title:"Section 1 — ThreadPoolExecutor", sub:"submit, map, as_completed, exceptions" },
  { id:"s2",    icon:"🔒", title:"Section 2 — Sync Primitives", sub:"Event, Semaphore, Condition, Queue" },
  { id:"s3",    icon:"👻", title:"Section 3 — Daemon Threads",  sub:"Background threads, exit behavior" },
  { id:"s4",    icon:"⚡", title:"Section 4 — Asyncio Basics",  sub:"Coroutines, gather, create_task" },
  { id:"s5",    icon:"🔐", title:"Section 5 — Async Sync",      sub:"asyncio.Lock, asyncio.Semaphore" },
  { id:"s6",    icon:"📬", title:"Section 6 — asyncio.Queue",   sub:"Async producer/consumer, join()" },
  { id:"s7",    icon:"🔀", title:"Section 7 — Threads + Async", sub:"run_in_executor, when to use what" },
  { id:"s8",    icon:"🎯", title:"Section 8 — Mixed Challenge", sub:"Rate-limited downloader, comparison" },
  { id:"disc",  icon:"💬", title:"Discussion Questions",        sub:"5 interview-style questions with answers" },
];

const sectionComponents = {
  s1: <S1/>, s2: <S2/>, s3: <S3/>, s4: <S4/>,
  s5: <S5/>, s6: <S6/>, s7: <S7/>, s8: <S8/>, disc: <SDiscussion/>,
};

// ── OVERVIEW ──────────────────────────────────────────────────────────────────

function Overview({ onNav }) {
  const cards = [
    { id:"s1", icon:"🏊", title:"Section 1 — ThreadPoolExecutor", sub:"submit, map, as_completed, exceptions", badges:["Easy","Easy","Medium","Medium"] },
    { id:"s2", icon:"🔒", title:"Section 2 — Sync Primitives",   sub:"Event, Semaphore, Condition, Queue",   badges:["Medium","Medium","Hard","Medium"] },
    { id:"s3", icon:"👻", title:"Section 3 — Daemon Threads",    sub:"Background threads, exit behavior",    badges:["Easy","Easy"] },
    { id:"s4", icon:"⚡", title:"Section 4 — Asyncio Basics",    sub:"Coroutines, gather, create_task",      badges:["Easy","Medium","Medium"] },
    { id:"s5", icon:"🔐", title:"Section 5 — Async Sync",        sub:"asyncio.Lock, asyncio.Semaphore",      badges:["Medium","Medium"] },
    { id:"s6", icon:"📬", title:"Section 6 — asyncio.Queue",     sub:"Async producer/consumer, join()",      badges:["Hard","Hard"] },
    { id:"s7", icon:"🔀", title:"Section 7 — Threads + Async",   sub:"run_in_executor, when to use what",    badges:["Medium","Medium"] },
    { id:"s8", icon:"🎯", title:"Section 8 — Mixed Challenge",   sub:"Rate-limited downloader, comparison",  badges:["Hard","Hard"] },
    { id:"disc",icon:"💬",title:"Discussion Questions",          sub:"5 interview-style questions with answers",badges:[] },
  ];
  return (
    <div>
      <div style={{marginBottom:8,fontSize:11,letterSpacing:2,textTransform:"uppercase",color:T.accent,fontWeight:700}}>
        PYTHON · THREADING · ADVANCED
      </div>
      <h1 style={{fontSize:26,fontWeight:800,color:T.textPri,margin:"0 0 10px",lineHeight:1.2}}>
        Multithreading (Advanced) &amp; Async — Exercises
      </h1>
      <p style={{fontSize:14,color:T.textSec,lineHeight:1.7,margin:"0 0 18px"}}>
        18 exercises covering: thread pools, synchronization primitives, daemon threads,
        and{" "}<code style={{color:T.accent,fontFamily:"monospace"}}>asyncio</code> (coroutines, tasks, async locks, queues, mixing with threads).
        <br/>Builds on Exercise Set 1 (threads, Lock, RLock).
      </p>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:24}}>
        {["Easy","Medium","Hard"].map(l => <Difficulty key={l} level={l} />)}
        <span style={{fontSize:12,color:T.textMute,alignSelf:"center",marginLeft:4}}>18 exercises · 5 discussion questions</span>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
        {cards.map(c => (
          <button key={c.id} onClick={()=>onNav(c.id)}
            style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,
              padding:"16px 18px",textAlign:"left",cursor:"pointer",transition:"border-color .15s"}}
            onMouseEnter={e=>e.currentTarget.style.borderColor=T.accent}
            onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
            <div style={{fontSize:22,marginBottom:8}}>{c.icon}</div>
            <div style={{fontSize:13.5,fontWeight:700,color:T.textPri,marginBottom:4}}>{c.title}</div>
            <div style={{fontSize:12,color:T.textSec,marginBottom:10}}>{c.sub}</div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
              {c.badges.map((b,i)=><Difficulty key={i} level={b}/>)}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── MAIN EXPORT ───────────────────────────────────────────────────────────────

export default function PythonThreadingExercises2() {
  const [active, setActive] = useState("home");
  const [visited, setVisited] = useState(new Set(["home"]));
  const mainRef = useRef(null);

  const totalSections = SECTIONS.length - 1;
  const visitedCount = visited.size;
  const pct = Math.round((visitedCount / totalSections) * 100);

  function nav(id) {
    setActive(id);
    setVisited(v => new Set([...v, id]));
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }

  return (
    <div style={{display:"flex",height:"calc(100vh - 70px)",background:T.bg,color:T.textPri,fontFamily:"system-ui,sans-serif",overflow:"hidden"}}>

      {/* Sidebar */}
      <div style={{width:260,minWidth:220,borderRight:`1px solid ${T.border}`,
        display:"flex",flexDirection:"column",background:T.surface,overflowY:"auto",flexShrink:0}}>
        <div style={{padding:"14px 14px 0"}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:1,textTransform:"uppercase",
            color:T.accent,marginBottom:10}}>📓 Exercise Set 2</div>
          <div style={{height:4,background:T.border,borderRadius:4,marginBottom:4}}>
            <div style={{height:"100%",borderRadius:4,background:`linear-gradient(90deg,${T.accent},${T.purple})`,
              width:`${pct}%`,transition:"width .3s"}}/>
          </div>
          <div style={{fontSize:10,color:T.textMute,marginBottom:12}}>{pct}% explored</div>
        </div>
        <nav style={{flex:1,padding:"0 8px 16px"}}>
          {SECTIONS.map(s => {
            const isActive = active===s.id;
            const isDone = visited.has(s.id) && !isActive;
            return (
              <button key={s.id} onClick={()=>nav(s.id)}
                style={{width:"100%",textAlign:"left",background:isActive?`${T.accent}15`:"none",
                  border:"none",borderRadius:8,padding:"8px 10px",cursor:"pointer",marginBottom:2,
                  borderLeft:isActive?`3px solid ${T.accent}`:"3px solid transparent"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:14}}>{s.icon}</span>
                  <span style={{fontSize:12.5,fontWeight:isActive?700:500,color:isActive?T.accent:T.textPri,
                    lineHeight:1.3,flex:1}}>{s.title}</span>
                  {isDone && <span style={{fontSize:10,color:T.green}}>✓</span>}
                </div>
                <div style={{fontSize:10.5,color:T.textMute,marginTop:1,paddingLeft:20}}>{s.sub}</div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main content */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* Progress bar — fixed above scroll area */}
        <div style={{flexShrink:0,background:T.bg,padding:"8px 40px 10px",
          borderBottom:`1px solid ${T.border}`,
          display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:14}}>{SECTIONS.find(s=>s.id===active)?.icon}</span>
          <span style={{fontSize:13,fontWeight:600,color:T.textPri}}>
            {active==="home"?"Python Threading — Advanced Exercises":SECTIONS.find(s=>s.id===active)?.title}
          </span>
          <div style={{flex:1,height:3,background:T.border,borderRadius:2}}>
            <div style={{height:"100%",borderRadius:2,background:`linear-gradient(90deg,${T.accent},${T.purple})`,
              width:`${pct}%`,transition:"width .3s"}}/>
          </div>
          <span style={{fontSize:11,color:T.textMute,minWidth:28,textAlign:"right"}}>{pct}%</span>
        </div>

        <div ref={mainRef} style={{flex:1,overflowY:"auto",padding:"32px 40px"}}>
          {active==="home"
            ? <Overview onNav={nav}/>
            : <div>
                <h2 style={{fontSize:20,fontWeight:700,color:T.textPri,margin:"0 0 20px"}}>
                  {SECTIONS.find(s=>s.id===active)?.title}
                </h2>
                {sectionComponents[active]}
                <div style={{display:"flex",justifyContent:"space-between",marginTop:32,paddingTop:16,
                  borderTop:`1px solid ${T.border}`}}>
                  {(() => {
                    const idx = SECTIONS.findIndex(s=>s.id===active);
                    const prev = SECTIONS[idx-1];
                    const next = SECTIONS[idx+1];
                    return (<>
                      {prev
                        ? <button onClick={()=>nav(prev.id)} style={{background:T.card,border:`1px solid ${T.border}`,
                            borderRadius:8,padding:"8px 16px",cursor:"pointer",fontSize:12,color:T.textSec,fontFamily:"inherit"}}>
                            ← {prev.title}
                          </button>
                        : <span/>}
                      {next
                        ? <button onClick={()=>nav(next.id)} style={{background:T.accent,border:"none",
                            borderRadius:8,padding:"8px 16px",cursor:"pointer",fontSize:12,color:"#0B0F1A",fontWeight:700,fontFamily:"inherit"}}>
                            {next.title} →
                          </button>
                        : <span/>}
                    </>);
                  })()}
                </div>
              </div>
          }
        </div>
      </div>
    </div>
  );
}
